import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
// import { SocketContext } from "../../context/Socket/SocketContext";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";

import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CompanyModal from "../../components/CompaniesModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import moment from "moment";
import ColorModeContext from "../../layout/themeContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANIES") {
    const companies = action.payload;
    const newCompanies = [];

    companies.forEach((company) => {
      const companyIndex = state.findIndex((u) => u.id === company.id);
      if (companyIndex !== -1) {
        state[companyIndex] = company;
      } else {
        newCompanies.push(company);
      }
    });

    return [...state, ...newCompanies];
  }

  if (action.type === "UPDATE_COMPANIES") {
    const company = action.payload;
    const companyIndex = state.findIndex((u) => u.id === company.id);

    if (companyIndex !== -1) {
      state[companyIndex] = company;
      return [...state];
    } else {
      return [company, ...state];
    }
  }

  if (action.type === "DELETE_COMPANIES") {
    const companyId = action.payload;

    const companyIndex = state.findIndex((u) => u.id === companyId);
    if (companyIndex !== -1) {
      state.splice(companyIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Companies = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [refreshingStorageId, setRefreshingStorageId] = useState(null);
  const [enteringSupportId, setEnteringSupportId] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [contactingCompanyId, setContactingCompanyId] = useState(null);
  // ventura-select-connection-10-07
  const [contactCompanyToOpen, setContactCompanyToOpen] = useState(null);
  const [companyContactConnections, setCompanyContactConnections] = useState([]);
  const [loadingContactConnections, setLoadingContactConnections] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [companies, dispatch] = useReducer(reducer, []);
  const { dateToClient, datetimeToClient } = useDate();

  // const { getPlanCompany } = usePlans();
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);
  const { mode } = useContext(ColorModeContext);
  const theme = useTheme();

  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error(
          "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
        );
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCompanies = async () => {
        try {
          const { data } = await api.get("/companiesPlan/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_COMPANIES", payload: data.companies });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchCompanies();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  // Somente Super Admin; sessao separada por aba (sessionStorage),
  // sem acesso a senha do cliente e sem historico de auditoria adicional.
  const handleEnterSupport = async company => {
    if (!user?.super || enteringSupportId !== null) return;
    if (!window.confirm(`Acessar ${company.name} em modo suporte por 30 minutos?`)) return;
    setEnteringSupportId(company.id);
    try {
      const { data } = await api.post(`/companies/${company.id}/support-session`);
      if (!data?.token) throw new Error("Token de suporte nao retornado");
      sessionStorage.setItem("venturaSupportToken", data.token);
      window.location.assign("/tickets");
    } catch (err) {
      toastError(err);
      setEnteringSupportId(null);
    }
  };

  const handleRefreshStorage = async company => {
    if (!user?.super || refreshingStorageId !== null) return;
    setRefreshingStorageId(company.id);
    try {
      const { data } = await api.get(`/companies/${company.id}/storage-metrics`);
      dispatch({
        type: "UPDATE_COMPANIES",
        payload: { ...company, metrics: data.metrics }
      });
    } catch (err) {
      toastError(err);
    } finally {
      setRefreshingStorageId(null);
    }
  };

  const handleOpenCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(true);
  };

  const handleCloseCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setCompanyModalOpen(true);
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success(i18n.t("compaies.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCompany(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderStatus = (row) => {
    return row.status === false ? "Não" : "Sim";
  };

  const renderPlanValue = (row) => {
    return row.planId !== null
      ? row.plan.amount
        ? row.plan.amount.toLocaleString("pt-br", { minimumFractionDigits: 2 })
        : "00.00"
      : "-";
  };

  const renderWhatsapp = (row) => {
    return row.useWhatsapp === false ? "Não" : "Sim";
  };

  const renderFacebook = (row) => {
    return row.useFacebook === false ? "Não" : "Sim";
  };

  const renderInstagram = (row) => {
    return row.useInstagram === false ? "Não" : "Sim";
  };

  const renderCampaigns = (row) => {
    return row.useCampaigns === false ? "Não" : "Sim";
  };

  const renderSchedules = (row) => {
    return row.useSchedules === false ? "Não" : "Sim";
  };

  const renderInternalChat = (row) => {
    return row.useInternalChat === false ? "Não" : "Sim";
  };

  const renderExternalApi = (row) => {
    return row.useExternalApi === false ? "Não" : "Sim";
  };

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { backgroundColor: "#fffead" };
      }
      if (diff <= 0) {
        return { backgroundColor: "#fa8c8c" };
      }
    }
    return {};
  };

  const cellStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" };
      }
      if (diff <= 0) {
        return { color: "#fff" };
      }
    }
    return {};
  };

  const formatFolderSize = (size) => {
    if (!size || size === 0) return '0 Bytes';
    
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    let index = 0;
    let formattedSize = size;

    while (formattedSize >= 1024 && index < units.length - 1) {
        formattedSize /= 1024;
        index++;
    }

    return `${formattedSize.toFixed(2)} ${units[index]}`;
};


  // ventura-direct-ticket-10-07
  // Fluxo normal da empresa autenticada, sem formulário intermediário.
  const normalizeCompanyPhone = value => {
    let digits = String(value || "").replace(/\D/g, "");

    if (digits.startsWith("0") && [11, 12].includes(digits.length)) {
      digits = digits.slice(1);
    }

    if ([10, 11].includes(digits.length)) {
      digits = `55${digits}`;
    }

    return digits;
  };

  const handleOpenContactCompany = async company => {
    if (contactingCompanyId !== null || loadingContactConnections) return;

    const number = normalizeCompanyPhone(company.phone);
    if (!/^55\d{10,11}$/.test(number)) {
      toast.error("Telefone inválido. Corrija o número da empresa, incluindo o DDD.");
      return;
    }

    setContactCompanyToOpen(company);
    setCompanyContactConnections([]);
    setLoadingContactConnections(true);

    try {
      const { data } = await api.get("/whatsapp", {
        params: { companyId: user.companyId, session: 0 }
      });

      const connections = Array.isArray(data)
        ? data
        : (Array.isArray(data?.whatsapps) ? data.whatsapps : []);

      const allowed = connections.filter(connection => {
        const channel = String(connection.channel || "whatsapp").toLowerCase();
        const sameCompany = connection.companyId == null ||
          Number(connection.companyId) === Number(user.companyId);

        return sameCompany &&
          ["whatsapp", "whatsapp_oficial"].includes(channel);
      });

      setCompanyContactConnections(allowed);
    } catch (error) {
      toast.error("Não foi possível carregar as conexões WhatsApp.");
      setContactCompanyToOpen(null);
    } finally {
      setLoadingContactConnections(false);
    }
  };

  const handleContactCompany = async (company, selectedWhatsappId) => {
    if (contactingCompanyId !== null) return;

    const selectedConnection = companyContactConnections.find(
      connection => Number(connection.id) === Number(selectedWhatsappId)
    );

    if (!selectedConnection ||
        String(selectedConnection.status || "").toUpperCase() !== "CONNECTED") {
      toast.error("Selecione uma conexão WhatsApp conectada.");
      return;
    }

    const rawNumber = String(company.phone || "").replace(/\D/g, "");
    const number = normalizeCompanyPhone(rawNumber);

    if (!/^55\d{10,11}$/.test(number)) {
      toast.error("Confira o telefone da empresa. É necessário um número brasileiro válido com DDD.");
      return;
    }

    setContactingCompanyId(company.id);
    let step = "localizar o contato";

    try {
      let contact = null;

      // Procurar pelos formatos com e sem o código 55.
      const searches = [...new Set([number, rawNumber])];

      for (const searchParam of searches) {
        const { data } = await api.get("/contacts", {
          params: { searchParam, pageNumber: 1 }
        });

        const found = Array.isArray(data)
          ? data
          : (Array.isArray(data?.contacts) ? data.contacts : []);

        contact = found.find(
          item => normalizeCompanyPhone(item.number) === number
        );

        if (contact) break;
      }

      if (!contact) {
        step = "cadastrar o contato automaticamente";
        const response = await api.post("/contacts", {
          name: company.name,
          number,
          email: company.email || ""
        });
        contact = response.data;
      }

      if (!contact?.id) {
        throw new Error("A API não retornou um contato válido.");
      }

      step = "abrir o atendimento";

      const queueId = user.queues?.length === 1
        ? user.queues[0].id
        : null;

      const { data: ticket } = await api.post("/tickets", {
        contactId: contact.id,
        status: "open",
        userId: user.id,
        queueId,
        whatsappId: String(selectedWhatsappId)
      });

      if (!ticket?.uuid) {
        throw new Error("A API não retornou a identificação do ticket.");
      }

      setContactCompanyToOpen(null);
      history.push(`/tickets/${ticket.uuid}`);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.ticket) {
        toast.error("Este contato já possui atendimento com outro atendente.");
      } else {
        const detail = error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Erro desconhecido";

        toast.error(`Não foi possível ${step}: ${String(detail).slice(0, 180)}`);
      }
    } finally {
      setContactingCompanyId(null);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCompany &&
          `${i18n.t("compaies.confirmationModal.deleteTitle")} ${
            deletingCompany.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCompany(deletingCompany.id)}
      >
        {i18n.t("compaies.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {/* Seleção da conexão para iniciar atendimento */}
      <Dialog
        open={Boolean(contactCompanyToOpen)}
        fullWidth
        maxWidth="xs"
        onClose={() => {
          if (contactingCompanyId === null && !loadingContactConnections) {
            setContactCompanyToOpen(null);
          }
        }}
      >
        <DialogTitle>Escolha a conexão WhatsApp</DialogTitle>
        <DialogContent dividers>
          <div style={{ marginBottom: 14 }}>
            {contactCompanyToOpen?.name}
          </div>
          {loadingContactConnections ? (
            <div>Carregando conexões...</div>
          ) : companyContactConnections.length === 0 ? (
            <div>Nenhuma conexão WhatsApp disponível nesta empresa.</div>
          ) : (
            companyContactConnections.map(connection => {
              const connected =
                String(connection.status || "").toUpperCase() === "CONNECTED";

              return (
                <Button
                  key={connection.id}
                  fullWidth
                  variant="outlined"
                  color="primary"
                  disabled={contactingCompanyId !== null || !connected}
                  style={{
                    marginBottom: 10,
                    padding: 12,
                    textTransform: "none"
                  }}
                  onClick={() =>
                    handleContactCompany(contactCompanyToOpen, connection.id)
                  }
                >
                  {contactingCompanyId !== null
                    ? "Abrindo atendimento..."
                    : `${connection.name} — ${
                        connection.channel === "whatsapp_oficial"
                          ? "API Oficial"
                          : "WhatsApp"
                      } (${connection.status || "Sem status"})`}
                </Button>
              );
            })
          )}
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
            Conexões desconectadas não podem ser selecionadas.
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            disabled={contactingCompanyId !== null || loadingContactConnections}
            onClick={() => setContactCompanyToOpen(null)}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
      <CompanyModal
        open={companyModalOpen}
        onClose={handleCloseCompanyModal}
        aria-labelledby="form-dialog-title"
        companyId={selectedCompany && selectedCompany.id}
      />
      <MainHeader>
        <Title>
          {i18n.t("compaies.title")} ({companies.length})
        </Title>
        {/* <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenCompanyModal}
                    >
                        {i18n.t("compaies.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper> */}
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("compaies.table.ID")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.email")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.namePlan")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.value")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.createdAt")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.dueDate")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.lastLogin")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.folderSize")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.totalFiles")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("compaies.table.lastUpdate")}
              </TableCell>
              {/* <TableCell align="center">{i18n.t("compaies.table.numberAttendants")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.numberConections")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.numberQueues")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.useWhatsapp")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.useFacebook")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.useInstagram")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.useCampaigns")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.useExternalApi")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.useInternalChat")}</TableCell> */}
              {/* <TableCell align="center">{i18n.t("compaies.table.useSchedules")}</TableCell> */}
              <TableCell align="center">Contato</TableCell>
              {/* <TableCell align="center">{i18n.t("compaies.table.actions")}</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {companies.map((company) => (
                <TableRow style={rowStyle(company)} key={company.id}>
                  <TableCell style={cellStyle(company)} align="center">{company.id}</TableCell>
                  <TableCell style={cellStyle(company)} align="center">
                    {renderStatus(company.status)}
                  </TableCell>
                  <TableCell style={cellStyle(company)} align="center">{company.name}</TableCell>
                  <TableCell style={cellStyle(company)} align="center">{company.email}</TableCell>
                  <TableCell style={cellStyle(company)} align="center">{company?.plan?.name}</TableCell>
                  <TableCell style={cellStyle(company)} align="center">
                    R$ {renderPlanValue(company)}
                  </TableCell>
                  <TableCell style={cellStyle(company)} align="center">
                    {dateToClient(company.createdAt)}
                  </TableCell>
                  <TableCell style={cellStyle(company)} align="center">
                    {dateToClient(company.dueDate)}
                    <br />
                    <span style={cellStyle(company)}>{company.recurrence}</span>
                  </TableCell>
                  <TableCell style={cellStyle(company)} align="center">
                    {datetimeToClient(company.lastLogin)}
                  </TableCell>
                  <TableCell style={cellStyle(company)} align="center">{company?.metrics?.folderSize == null
                      ? "Não calculado"
                      : formatFolderSize(company.metrics.folderSize)}
                    <Button
                      size="small"
                      disabled={refreshingStorageId !== null}
                      onClick={() => handleRefreshStorage(company)}
                    >
                      {refreshingStorageId === company.id ? "Calculando..." : "Atualizar"}
                    </Button></TableCell>
                  <TableCell style={cellStyle(company)} align="center">
                    {company?.metrics?.numberOfFiles == null ? "—" : company.metrics.numberOfFiles}
                  </TableCell>
                  <TableCell style={cellStyle(company)} align="center">
                    {company?.metrics?.lastUpdate
                      ? datetimeToClient(company.metrics.lastUpdate)
                      : "Não calculado"}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={contactingCompanyId !== null}
                      onClick={() => handleOpenContactCompany(company)}
                    >
                      {contactingCompanyId === company.id
                        ? "Abrindo..."
                        : "Entrar em contato"}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      style={{ marginLeft: 8, textTransform: "none" }}
                      disabled={enteringSupportId !== null}
                      onClick={() => handleEnterSupport(company)}
                    >
                      {enteringSupportId === company.id ? "Acessando..." : "Acessar empresa"}
                    </Button>
                  </TableCell>
                  {/* <TableCell align="center">{company.plan.users}</TableCell> */}
                  {/* <TableCell align="center">{company.plan.connections}</TableCell> */}
                  {/* <TableCell align="center">{company.plan.queues}</TableCell> */}
                  {/* <TableCell align="center">{renderWhatsapp(company.plan.useWhatsapp)}</TableCell> */}
                  {/* <TableCell align="center">{renderFacebook(company.plan.useFacebook)}</TableCell> */}
                  {/* <TableCell align="center">{renderInstagram(company.plan.useInstagram)}</TableCell> */}
                  {/* <TableCell align="center">{renderCampaigns(company.plan.useCampaigns)}</TableCell> */}
                  {/* <TableCell align="center">{renderExternalApi(company.plan.useExternalApi)}</TableCell> */}
                  {/* <TableCell align="center">{renderInternalChat(company.plan.useInternalChat)}</TableCell> */}
                  {/* <TableCell align="center">{renderSchedules(company.plan.useSchedules)}</TableCell> */}
                  {/* <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditCompany(company)}
                                        >
                                            <EditIcon />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                setConfirmModalOpen(true);
                                                setDeletingCompany(company);
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </TableCell> */}
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Companies;